'use client';

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import type {
  BookPayload,
  CardLabels,
  EnquirePayload,
  FlatPackageItem,
} from '../../types';
import PackageCard from '../PackageCard';
import CarouselRow from './CarouselRow';

interface CarouselProps {
  items: FlatPackageItem[];
  labels?: CardLabels;
  onEnquire?: (payload: EnquirePayload) => void;
  onBook?: (payload: BookPayload) => void;
}

// Matches tailwind.config.js `lg` breakpoint (976px) — the desktop grid
// switches from 2 to 3 columns there, so row-chunking must match it.
const LG_QUERY = '(min-width: 976px)';

const subscribeLg = (callback: () => void): (() => void) => {
  const mq = window.matchMedia(LG_QUERY);
  mq.addEventListener('change', callback);
  return () => mq.removeEventListener('change', callback);
};

const getLgSnapshot = (): boolean => window.matchMedia(LG_QUERY).matches;

const getLgServerSnapshot = (): boolean => false;

function useIsLg(): boolean {
  return useSyncExternalStore(subscribeLg, getLgSnapshot, getLgServerSnapshot);
}

function chunk<T>(items: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size));
  }
  return rows;
}

export default function Carousel({
  items,
  labels,
  onEnquire,
  onBook,
}: CarouselProps): React.ReactElement {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(0);
  const [pageWidth, setPageWidth] = useState(0);
  const isLg = useIsLg();

  const keyed = useMemo(
    () => items.map((pkg) => ({ key: pkg.id, pkg })),
    [items],
  );

  const desktopRows = useMemo(() => chunk(keyed, isLg ? 3 : 2), [keyed, isLg]);

  useEffect((): (() => void) => {
    const handle = (): void => {
      if (!trackRef.current) return;
      const w = trackRef.current.clientWidth || 0;
      setPageWidth(w);
      trackRef.current.scrollTo({ left: w * active, behavior: 'auto' });
    };

    handle();
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, [active]);

  const onScroll = (): void => {
    if (!trackRef.current || !pageWidth) return;
    const idx = Math.round(trackRef.current.scrollLeft / pageWidth);
    if (idx !== active) setActive(idx);
  };

  const goTo = (idx: number): void => {
    if (!trackRef.current || !pageWidth) return;
    trackRef.current.scrollTo({ left: idx * pageWidth, behavior: 'smooth' });
  };

  if (keyed.length === 0) return <></>;

  return (
    <>
      {/* Mobile carousel */}
      <div className="mt-4 md:mt-6 md:hidden">
        <div
          ref={trackRef}
          onScroll={onScroll}
          className="relative -mx-4 snap-x snap-mandatory overflow-x-auto px-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <div className="flex gap-4">
            {keyed.map(({ key, pkg }) => (
              <div key={key} className="mb-2 w-full shrink-0 snap-start">
                <PackageCard
                  badge={pkg.badge}
                  title={pkg.title}
                  tests={pkg.tests}
                  testType={pkg.testType}
                  gender={pkg.gender}
                  price={pkg.price}
                  defaultOpen={false}
                  labels={labels}
                  onEnquire={() =>
                    onEnquire?.({
                      category: pkg.title,
                      title: pkg.title,
                      gender: pkg.gender,
                      testType: pkg.testType,
                      price: pkg.price,
                      source: 'health_packages_most_popular',
                    })
                  }
                  onBook={
                    pkg.apiIds?.length
                      ? () =>
                          onBook?.({
                            category: pkg.title,
                            title: pkg.title,
                            gender: pkg.gender,
                            testType: pkg.testType,
                            price: pkg.price,
                            source: 'health_packages_most_popular',
                            apiIds: pkg.apiIds,
                            depositFee: pkg.depositFee,
                          })
                      : undefined
                  }
                />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-2 flex items-center justify-center">
          {keyed.map((_, i) => (
            <button
              key={`dot-${i}`}
              onClick={() => goTo(i)}
              aria-label={`Go to card ${i + 1}`}
              aria-current={active === i ? 'true' : undefined}
              className="flex size-11 items-center justify-center"
            >
              <span
                aria-hidden="true"
                className={`h-2 rounded-full transition-all ${
                  active === i
                    ? 'bg-surface-info-indicator w-6'
                    : 'bg-surface-indicator-muted w-3'
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Desktop grid */}
      <div className="mt-6 hidden gap-6 md:flex md:flex-col">
        {desktopRows.map((row, ri) => (
          <CarouselRow
            key={ri}
            row={row}
            labels={labels}
            onEnquire={onEnquire}
            onBook={onBook}
          />
        ))}
      </div>
    </>
  );
}
